// src/pages/WebQRCode.js
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";

const BASE_URL = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com/api";

export default function WebQRCode() {
  const stallID = localStorage.getItem("stallID");
  const stallName = localStorage.getItem("username");
  const [logoUrl, setLogoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const fileInputRef = useRef(null);

  // ✅ Load logo on component mount
  useEffect(() => {
    loadLogo();
  }, []);
const loadLogo = async () => {
  const extensions = ["jpg", "png", "jpeg", "gif", "svg"];
  let found = false;

  for (let ext of extensions) {
    const testUrl = `${BASE_URL}/foods/uploads/mummy-logo.${ext}?t=${Date.now()}`;

    try {
      const response = await fetch(testUrl, { method: "HEAD" });
      if (response.ok) {
        setLogoUrl(testUrl);
        setUploadStatus(`✅ Logo loaded (${ext})`);
        found = true;
        break;
      }
    } catch (err) {
      // just try next extension
    }
  }

  if (!found) {
    setLogoUrl("");
    setUploadStatus("❌ No logo found (jpg/png/jpeg/gif/svg not found)");
  }
};

  /*
  const loadLogo = async () => {
    const url = `${BASE_URL}/foods/uploads/mummy-logo.jpg?t=${Date.now()}`;

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      setLogoUrl(url);
      setUploadStatus("✅ Logo loaded successfully");
    };

    img.onerror = () => {
      setUploadStatus("❌ No logo found");
    };

    img.src = url;
  };
*/
  // ✅ Handle file selection (preview only)
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setLogoUrl(previewUrl);
    setUploadStatus("📸 Preview ready - Click 'Upload Logo' to save");
  };

  // ✅ Handle file upload to backend
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus("❌ Please select a file first");
      return;
    }

    setUploadStatus("⏳ Uploading...");

    try {
      const formData = new FormData();
      formData.append("stallID", stallID);
      formData.append("file", selectedFile);

      const response = await axios.post(`${BASE_URL}/api/admin/uploadLogo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 15000,
      });

      if (response.data.success) {
        setUploadStatus("✅ Upload successful! Loading new logo...");

        // Clear file input and reload logo
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Wait a bit for the file to be saved, then reload
        setTimeout(() => {
          setLogoUrl(""); // Clear current logo
          loadLogo(); // Reload from server
        }, 1000);

      } else {
        setUploadStatus("❌ Upload failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Upload error:", error);
      let errorMessage = "Upload failed: ";

      if (error.response) {
        errorMessage += `Server error (${error.response.status}): ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage += "No response from server. Check if backend is running.";
      } else {
        errorMessage += error.message;
      }

      setUploadStatus(errorMessage);
    }
  };

  // ✅ Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // ✅ Enhanced image component
  const LogoImage = () => {
    if (!logoUrl) {
      return (
        <div style={{
          width: "120px",
          height: "120px",
          margin: "0 auto",
          border: "2px dashed #ccc",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
          backgroundColor: "#f8f9fa"
        }}>
          No Logo
        </div>
      );
    }

    return (
      <img
        src={logoUrl}
        alt="Stall Logo"
        crossOrigin="anonymous"
        style={{
          width: "120px",
          height: "120px",
          objectFit: "contain",
          borderRadius: "8px",
          border: "2px solid #ddd",
          display: "block",
          margin: "0 auto"
        }}
        onLoad={() => {
          console.log("✅ Logo displayed successfully!");
        }}
        onError={() => {
          console.error("❌ Failed to display logo");
        }}
      />
    );
  };

  // ✅ Force reload logo
  const forceReloadLogo = () => {
    setLogoUrl("");
    setUploadStatus("Reloading logo...");
    setTimeout(loadLogo, 500);
  };

  // ✅ Download QR code
  const handleDownload = async () => {
    const qrDiv = document.getElementById("qr-container");
    if (!qrDiv) {
      alert("QR container not found");
      return;
    }

    try {
      setUploadStatus("Generating download...");
      const canvas = await html2canvas(qrDiv, {
        useCORS: true,
        scale: 3,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `${stallName.replace(/\s+/g, '_')}_QRCode.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setUploadStatus("✅ Download started!");
    } catch (err) {
      console.error("Download error:", err);
      setUploadStatus("❌ Download failed");
    }
  };

  return (
    <div className="container text-center mt-4">
      {/* QR Code Container */}
      <div
        id="qr-container"
        style={{
          width: "420px",
          padding: "25px",
          background: "#fff",
          borderRadius: "12px",
          border: "6px solid #ff6b6b",
          boxShadow: "0 0 15px rgba(0,0,0,0.15)",
          display: "inline-block",
          textAlign: "center",
          marginBottom: "20px"
        }}
      >
        {/* Stall Name */}
        <h3 style={{
          fontWeight: "bold",
          marginBottom: "20px",
          color: "#d63031",
          fontSize: "24px",
          textTransform: "uppercase"
        }}>
          {stallName}
        </h3>

        {/* QR Code */}
        <QRCodeCanvas
          value={`${window.location.origin}/MobileOrderPage?stallID=${stallID}`}
          size={200}
          level="H"
          includeMargin={true}
        />

        {/* Logo */}
        <div style={{ marginTop: "20px", minHeight: "120px" }}>
          <LogoImage />
        </div>

        {/* Instruction */}
        <p style={{
          fontWeight: "bold",
          fontSize: "18px",
          marginTop: "15px",
          color: "#333",
          marginBottom: "0"
        }}>
          SCAN TO ORDER FOOD
        </p>
      </div>

      {/* Status Message */}
      {uploadStatus && (
        <div className={`alert ${uploadStatus.includes('✅') ? 'alert-success' : uploadStatus.includes('❌') ? 'alert-danger' : 'alert-warning'}`}>
          {uploadStatus}
        </div>
      )}

      {/* Upload Controls */}
      <div className="card mt-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Logo Upload</h5>
        </div>
        <div className="card-body">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          <div className="row align-items-center">
            <div className="col-md-6 mb-2">
              <button
                className="btn btn-outline-primary w-100"
                onClick={triggerFileInput}
              >
                📁 Choose Image File
              </button>
              {selectedFile && (
                <div className="mt-2 text-muted small">
                  Selected: {selectedFile.name}
                </div>
              )}
            </div>

            <div className="col-md-6 mb-2">
              <button
                className="btn btn-success w-100"
                onClick={handleUpload}
                disabled={!selectedFile}
              >
                {uploadStatus.includes("Uploading") ? "⏳ Uploading..." : "⬆️ Upload Logo"}
              </button>
            </div>
          </div>

          <div className="mt-2">
            <small className="text-muted">
              File will be saved as: <code>{stallID}-logo.jpg</code>
            </small>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-3">
        <div className="btn-group" role="group">
          <button className="btn btn-info me-2" onClick={forceReloadLogo}>
            🔄 Reload Logo
          </button>
          <button className="btn btn-success me-2" onClick={handleDownload}>
            📥 Download QR Code
          </button>
          <button
            className="btn btn-warning"
            onClick={() => window.open(`${BASE_URL}/foods/uploads/mummy-logo.jpg`, '_blank')}
          >
            🔗 Test Logo URL
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="alert alert-info mt-3">
        <strong>How to upload:</strong>
        <ol className="mb-0 text-start">
          <li>Click "Choose Image File" to select a logo</li>
          <li>Preview will show immediately</li>
          <li>Click "Upload Logo" to save to server</li>
          <li>Logo will automatically reload after upload</li>
        </ol>
      </div>
    </div>
  );
}
